import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Upload, Check, Pen, Trash2, Save, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import API from '../../services/api';

/**
 * JobCompletionModal - Universal job completion workflow with photos & signatures
 * Works for ALL service types: Rides, Cleaning, Laundry, Courier, Errands
 */
const JobCompletionModal = ({ 
  isOpen, 
  onClose, 
  job, 
  serviceType,
  onComplete,
  requireSignature = true,
  requirePhoto = true,
  checklist = null // Optional checklist items
}) => {
  const { token } = useAuth();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // State
  const [step, setStep] = useState(1); // 1: Photos, 2: Signature, 3: Review
  const [photos, setPhotos] = useState([]);
  const [signature, setSignature] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [notes, setNotes] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [completedChecklist, setCompletedChecklist] = useState([]);
  
  // Canvas drawing state
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  
  // Initialize canvas on mount
  useEffect(() => {
    if (step === 2 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [step]);
  
  // Photo handling
  const handlePhotoCapture = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: event.target.result,
          type: 'proof',
          caption: '',
          file
        }]);
      };
      reader.readAsDataURL(file);
    });
  };
  
  const removePhoto = (id) => {
    setPhotos(prev => prev.filter(p => p.id !== id));
  };
  
  // Signature canvas drawing
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };
  
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCanvasCoords(e);
    setLastPos(coords);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e);
    
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    
    setLastPos(coords);
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };
  
  const saveSignature = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    setSignature(dataUrl);
  };
  
  // Submit completion
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Prepare completion data
      const completionData = {
        job_id: job.id,
        service_type: serviceType,
        photos: photos.map(p => ({
          photo_data: p.data,
          photo_type: p.type,
          caption: p.caption,
          content_type: p.file?.type || 'image/jpeg'
        })),
        signature_data: signature,
        signer_name: signerName,
        completion_notes: notes,
        customer_satisfied: true,
        checklist_completed: completedChecklist.length,
        checklist_total: checklist?.length || 0,
        end_time: new Date().toISOString()
      };
      
      // Add service-specific data
      if (serviceType === 'rides') {
        completionData.distance_traveled = job.distance;
        if (job.dropoff_location) {
          completionData.end_location = {
            lat: job.dropoff_location.lat,
            lng: job.dropoff_location.lng
          };
        }
      }
      
      if (serviceType === 'courier') {
        completionData.delivery_confirmed = true;
        completionData.recipient_name = signerName;
      }
      
      const response = await API.post('/job-completion/complete', completionData);
      
      if (response.data) {
        onComplete?.(response.data);
        onClose();
      }
    } catch (err) {
      console.error('Completion error:', err);
      setError(err.response?.data?.detail || 'Failed to complete job');
    } finally {
      setLoading(false);
    }
  };
  
  // Step navigation
  const canProceed = () => {
    if (step === 1) {
      return !requirePhoto || photos.length > 0;
    }
    if (step === 2) {
      return !requireSignature || (signature && signerName.trim());
    }
    return true;
  };
  
  const nextStep = () => {
    if (step === 1 && !requireSignature) {
      setStep(3);
    } else if (step < 3) {
      setStep(step + 1);
    }
  };
  
  const prevStep = () => {
    if (step === 3 && !requireSignature) {
      setStep(1);
    } else if (step > 1) {
      setStep(step - 1);
    }
  };
  
  if (!isOpen) return null;
  
  const serviceLabels = {
    rides: 'Ride',
    laundry: 'Laundry',
    courier: 'Delivery',
    errands: 'Errand'
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Complete {serviceLabels[serviceType] || 'Job'}</h2>
            <p className="text-sm text-slate-400">Job #{job?.reference_number || job?.id?.slice(-6)}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Progress Steps */}
        <div className="px-4 py-3 border-b border-slate-700">
          <div className="flex items-center justify-center gap-2">
            {[
              { num: 1, label: 'Photos' },
              ...(requireSignature ? [{ num: 2, label: 'Signature' }] : []),
              { num: requireSignature ? 3 : 2, label: 'Review' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center gap-2 ${
                  step === s.num 
                    ? 'text-emerald-400' 
                    : step > s.num 
                      ? 'text-emerald-500' 
                      : 'text-slate-500'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === s.num 
                      ? 'bg-emerald-500/20 border-2 border-emerald-500' 
                      : step > s.num 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-700'
                  }`}>
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  <span className="text-sm hidden sm:inline">{s.label}</span>
                </div>
                {i < (requireSignature ? 2 : 1) && (
                  <div className={`w-8 h-0.5 ${step > s.num ? 'bg-emerald-500' : 'bg-slate-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          {/* Step 1: Photos */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center">
                <Camera className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                <h3 className="text-lg font-semibold text-white mb-1">Proof of Work Photos</h3>
                <p className="text-sm text-slate-400">
                  {requirePhoto 
                    ? 'Take at least one photo showing the completed work'
                    : 'Optional: Add photos of the completed work'}
                </p>
              </div>
              
              {/* Photo Grid */}
              <div className="grid grid-cols-3 gap-2">
                {photos.map(photo => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden group">
                    <img 
                      src={photo.data} 
                      alt="Proof" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removePhoto(photo.id)}
                      className="absolute top-1 right-1 p-1.5 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${photo.id}`}
                    >
                      <Trash2 className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
                
                {/* Add Photo Button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square rounded-lg border-2 border-dashed border-slate-600 hover:border-emerald-500 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-emerald-400 transition-colors"
                  data-testid="add-photo-btn"
                >
                  <Upload className="w-6 h-6" />
                  <span className="text-xs">Add Photo</span>
                </button>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={handlePhotoCapture}
                className="hidden"
              />
              
              {/* Checklist (if provided) */}
              {checklist && checklist.length > 0 && (
                <div className="mt-4 p-3 bg-slate-800 rounded-lg">
                  <h4 className="text-sm font-medium text-white mb-2">Completion Checklist</h4>
                  <div className="space-y-2">
                    {checklist.map((item, i) => (
                      <label key={i} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={completedChecklist.includes(i)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCompletedChecklist(prev => [...prev, i]);
                            } else {
                              setCompletedChecklist(prev => prev.filter(x => x !== i));
                            }
                          }}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                        />
                        <span className="text-sm text-slate-300">{item}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Step 2: Signature */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center">
                <Pen className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                <h3 className="text-lg font-semibold text-white mb-1">Customer Signature</h3>
                <p className="text-sm text-slate-400">
                  Have the customer sign below to confirm service completion
                </p>
              </div>
              
              {/* Signer Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter customer's name"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                  data-testid="signer-name-input"
                />
              </div>
              
              {/* Signature Canvas */}
              <div className="relative">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={200}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full h-48 bg-white rounded-lg border-2 border-slate-600 cursor-crosshair touch-none"
                  data-testid="signature-canvas"
                />
                {!signature && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-slate-400 text-sm">Sign here</span>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={clearSignature}
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="clear-signature"
                >
                  Clear
                </Button>
                <Button
                  onClick={saveSignature}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={!signerName.trim()}
                  data-testid="save-signature"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Signature
                </Button>
              </div>
              
              {signature && (
                <div className="flex items-center gap-2 text-emerald-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>Signature saved for {signerName}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
                <h3 className="text-lg font-semibold text-white mb-1">Review & Complete</h3>
                <p className="text-sm text-slate-400">
                  Review the details before marking this job complete
                </p>
              </div>
              
              {/* Summary */}
              <div className="space-y-3">
                {/* Photos Summary */}
                <div className="p-3 bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-300">Photos</span>
                    <span className={`text-sm ${photos.length > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {photos.length} uploaded
                    </span>
                  </div>
                  {photos.length > 0 && (
                    <div className="flex gap-1 overflow-x-auto">
                      {photos.slice(0, 4).map(photo => (
                        <img
                          key={photo.id}
                          src={photo.data}
                          alt="Proof"
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                        />
                      ))}
                      {photos.length > 4 && (
                        <div className="w-12 h-12 rounded bg-slate-700 flex items-center justify-center text-xs text-slate-400">
                          +{photos.length - 4}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Signature Summary */}
                {requireSignature && (
                  <div className="p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-300">Signature</span>
                      <span className={`text-sm ${signature ? 'text-emerald-400' : 'text-slate-500'}`}>
                        {signature ? 'Collected' : 'Not collected'}
                      </span>
                    </div>
                    {signature && (
                      <div className="flex items-center gap-2">
                        <img src={signature} alt="Signature" className="h-10 bg-white rounded" />
                        <span className="text-sm text-slate-400">- {signerName}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Checklist Summary */}
                {checklist && checklist.length > 0 && (
                  <div className="p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-300">Checklist</span>
                      <span className={`text-sm ${completedChecklist.length === checklist.length ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {completedChecklist.length}/{checklist.length} completed
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Completion Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any notes about the completed work..."
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none resize-none"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex gap-2">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={prevStep}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="step-back"
            >
              Back
            </Button>
          )}
          
          <Button
            onClick={step === 3 ? handleSubmit : nextStep}
            disabled={!canProceed() || loading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            data-testid={step === 3 ? "complete-submit" : "step-continue"}
          >
            {loading ? (
              <>
                <Clock className="w-4 h-4 mr-2 animate-spin" />
                Completing...
              </>
            ) : step === 3 ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark Complete
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobCompletionModal;
